import { useContext } from 'react'

import { OptimizelyContext } from '@optimizely/react-sdk'
import BundleCard from '@space-core/components/customCard/bundle/desktop'
import BundleCardMobile from '@space-core/components/customCard/bundle/mobile'
import type { BundleCard as BundleCardProps } from '@space-core/components/customCard/bundle/types'
import { ImageOrientation } from '@space-core/components/customImage/enum'
import Grid from '@space-core/components/grid'
import { GridDisplay } from '@space-core/components/grid/enum'
import { Actions, Entities } from '@space-core/datahub'
import { useManageModal } from '@space-core/hooks'
import { useMediaQuery } from '@space-core/hooks'
import { ScreenSizes } from '@space-core/styles'
import { GridUtils } from '@space-core/utils/grid'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import { useGoogleAnalytics } from '@space-shell/common/hooks/useGoogleAnalytics'

import useRequest from '@space-shell/common/hooks/useRequest'
import useThemeContext from '@space-shell/common/hooks/useThemeContext'
import { BundleEndpoints, BundleService } from '@space-shell/common/services/bundle'
import ModalBundle from '@space-shell/modules/bundle/modal'
import { useParams } from 'react-router-dom'

import styled from 'styled-components'

import { MAX_ITEMS_CAROUSEL } from '../constants'
import { useBundle } from '../context'

export type BundleProps = {
  bundles: BundleCardProps[]
}

const CustomGrid = styled(Grid)`
  grid-row-gap: var(--spacer-7);
  grid-column-gap: var(--spacer-4);
`

const BundleSection = ({ bundles }: BundleProps) => {
  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.lg})`)
  const { bundle, methods } = useBundle()
  const { membership } = useParams()
  const membershipSlug = membership as string
  const { theme } = useThemeContext()
  const { sendDataHubEvent } = useDataHubEvents()
  const { sendGoogleAnalyticsEvent } = useGoogleAnalytics()
  const { optimizely } = useContext(OptimizelyContext)

  const lengthBundle = bundles.length

  const { data: products } = useRequest(
    [BundleEndpoints.products(bundle.id, membershipSlug)],
    () => BundleService.getProducts(bundle.id, membershipSlug),
    { enabled: !!bundle.id }
  )

  const isCarousel =
    lengthBundle && lengthBundle <= MAX_ITEMS_CAROUSEL && lengthBundle > 1 ? true : false

  const {
    isModalOpen: isBundleModalOpen,
    handleOpen: handleOpenBundleModal,
    handleClose: handleCloseModal
  } = useManageModal()

  const sendEvents = (bundle: BundleCardProps) => {
    sendDataHubEvent({
      group: Entities.PRODUCTS_BUNDLE,
      name: Actions.CLICK_PRODUCT,
      params: {
        product: {
          id: bundle.id,
          name: bundle.name
        }
      }
    })

    sendGoogleAnalyticsEvent({
      group: Entities.PRODUCTS_BUNDLE,
      name: Actions.CLICK_PRODUCT,
      label: `bundleId:${bundle?.id}`
    })
  }

  const handleClickBundle = (bundle: BundleCardProps) => {
    methods.setBundle(bundle)
    sendEvents(bundle)
    handleOpenBundleModal()
    if (isMobile) {
      optimizely?.track('click_bundle_products')
    }
  }

  return (
    <>
      <CustomGrid
        className='_mt-5'
        cardOrientation={GridUtils.cardOrientation(
          String(ImageOrientation.HORIZONTAL.toUpperCase()),
          Number(lengthBundle)
        )}
        gridOrientation={GridUtils.gridOrientation(
          String(ImageOrientation.HORIZONTAL.toUpperCase())
        )}
        type={isMobile ? GridDisplay.MOBILE : GridDisplay.DESKTOP}
        isCarousel={isCarousel}>
        {bundles.map((bundle, index) => {
          return isMobile ? (
            <BundleCardMobile
              key={index}
              bundle={bundle}
              handleClick={() => handleClickBundle(bundle)}
              isCarousel={isCarousel}
              containerOrientation={GridUtils.containerOrientation(
                String(ImageOrientation.HORIZONTAL.toUpperCase())
              )}
              imageOrientation={GridUtils.imageOrientation(
                String(ImageOrientation.HORIZONTAL.toUpperCase())
              )}
              themeMode={theme}
            />
          ) : (
            <BundleCard
              key={index}
              bundle={bundle}
              handleClick={() => handleClickBundle(bundle)}
              themeMode={theme}
              items={lengthBundle}
              orientation={GridUtils.cardOrientation(
                String(ImageOrientation.HORIZONTAL.toUpperCase()),
                Number(lengthBundle)
              )}
              containerOrientation={GridUtils.containerOrientation(
                String(ImageOrientation.HORIZONTAL.toUpperCase())
              )}
              imageOrientation={GridUtils.imageOrientation(
                String(ImageOrientation.HORIZONTAL.toUpperCase())
              )}
            />
          )
        })}
      </CustomGrid>

      {bundle.id && products && (
        <ModalBundle
          products={products}
          bundle={bundle}
          show={isBundleModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </>
  )
}

export default BundleSection
